import { useCallback } from 'react';
import { trpc } from '../lib/trpc';
import { useIntegrationStore } from '../lib/integration-store';
import type { CreateIntegrationInput, UpdateIntegrationInput } from '../lib/integration-types';

export function useIntegrations() {
  const { integrations, providers, loading, error, setIntegrations, setProviders, addIntegration, updateIntegration, removeIntegration, setLoading, setError } = useIntegrationStore();

  const fetchIntegrations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await trpc.integration.list.query();
      setIntegrations(result as any);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch integrations');
    } finally {
      setLoading(false);
    }
  }, [setIntegrations, setLoading, setError]);

  const fetchProviders = useCallback(async () => {
    try {
      const result = await trpc.integration.providers.query();
      setProviders(result as any);
    } catch (err: any) {
      setError(err.message);
    }
  }, [setProviders, setError]);

  const createIntegration = useCallback(async (input: CreateIntegrationInput) => {
    setLoading(true);
    try {
      const integration = await trpc.integration.create.mutate(input);
      addIntegration(integration as any);
      return integration;
    } catch (err: any) {
      setError(err.message || 'Failed to create integration');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [addIntegration, setLoading, setError]);

  const updateIntegrationData = useCallback(async (id: string, data: UpdateIntegrationInput) => {
    try {
      const updated = await trpc.integration.update.mutate({ id, data });
      updateIntegration(id, data as any);
      return updated;
    } catch (err: any) {
      setError(err.message || 'Failed to update integration');
      throw err;
    }
  }, [updateIntegration, setError]);

  const deleteIntegration = useCallback(async (id: string) => {
    try {
      await trpc.integration.delete.mutate({ id });
      removeIntegration(id);
    } catch (err: any) {
      setError(err.message || 'Failed to delete integration');
      throw err;
    }
  }, [removeIntegration, setError]);

  const connectIntegration = useCallback(async (integrationId: string) => {
    try {
      const { url } = await trpc.integration.getOAuthUrl.mutate({ integrationId });
      // In production, this would redirect to the OAuth URL
      // For now, we just return it
      return { url };
    } catch (err: any) {
      setError(err.message || 'Failed to get OAuth URL');
      throw err;
    }
  }, [setError]);

  const disconnectIntegration = useCallback(async (id: string) => {
    try {
      const updated = await trpc.integration.disconnect.mutate({ id });
      updateIntegration(id, { status: 'disconnected' } as any);
      return updated;
    } catch (err: any) {
      setError(err.message || 'Failed to disconnect');
      throw err;
    }
  }, [updateIntegration, setError]);

  const testIntegration = useCallback(async (id: string) => {
    try {
      return await trpc.integration.test.mutate({ id });
    } catch (err: any) {
      setError(err.message || 'Failed to test integration');
      throw err;
    }
  }, [setError]);

  return {
    integrations,
    providers,
    loading,
    error,
    fetchIntegrations,
    fetchProviders,
    createIntegration,
    updateIntegration: updateIntegrationData,
    deleteIntegration,
    connectIntegration,
    disconnectIntegration,
    testIntegration,
  };
}
